// LAS MUESTRAS DE LA CASA: qué suena con un fichero y no con un oscilador. Números puros.
//
//     node scripts/prueba_muestras.mjs            (desde frontend/)
//     node scripts/prueba_muestras.mjs --prueba   y que las reglas muerden
//
// QUÉ PASÓ EL 13 DE OCTUBRE DE 2026. Gabri, después de jugar el combate: *«estoy insatisfecho
// con los sonidos y efectos que hacemos»*. Y al ir a mirarlo, el diagnóstico no era el que él
// esperaba —que faltara una biblioteca—: era que **no había un solo fichero de audio en la
// plataforma**. Todo sonaba con `lib/sound.js`, que sintetiza en vivo con un oscilador y un
// ruido filtrado. Un seno con una envolvente suena a calculadora por bien afinado que esté, y
// en vivo no hay tiempo ni para capas, ni para reverb, ni para repetir hasta que quede.
//
// LO QUE ESTE FICHERO ES: la tabla de a qué sonido de la casa le corresponde qué fichero. Nada
// más. No carga, no suena y no sabe de WebAudio —eso es `bancoDeSonidos.mjs`—, y por eso se
// puede comprobar con `node`.
//
// Y LA GRACIA ESTÁ EN QUE LAS CLAVES SON LAS DE SIEMPRE. `acierto`, `whoosh`, `punch`,
// `hit8bit`... son los sonidos que la casa ya pedía por su nombre desde hace meses. Poner aquí
// un fichero con ese nombre **hace que suene mejor en los veinte sitios que lo piden**, sin
// tocar una línea de quien lo pide. El que no esté en esta tabla sigue sintetizándose como
// siempre, y el día que una muestra no cargue, también: la casa nunca se queda muda.

import { CANAL } from "./canalesDeSonido.mjs";

/** Dónde viven los ficheros, servidos tal cual desde `public/`. */
export const CARPETA = "/audio/";

/**
 * LOS DOS FORMATOS, en orden de preferencia, con el tipo que hay que preguntarle al navegador.
 *
 * AAC PRIMERO PORQUE EL IPHONE ES EL QUE MANDA AQUÍ: Safari lleva años con AAC y sólo entiende
 * Vorbis de prestado. Y Vorbis detrás porque un Firefox de Linux puede venir sin los códecs de
 * pago del sistema, y entonces el m4a no suena **y no da ningún error**: devuelve una cadena
 * vacía al preguntarle, que es lo que comprueba `tipoQueSuena`.
 */
export const FORMATOS = [
  { ext: "m4a", tipo: 'audio/mp4; codecs="mp4a.40.2"' },
  { ext: "ogg", tipo: 'audio/ogg; codecs="vorbis"' },
];

/**
 * LA TABLA. `canal` es el de `canalesDeSonido.mjs` y **tiene que coincidir** con el que ya
 * tuviera ese sonido allí: lo comprueba el portero, porque dos tablas que dicen cosas distintas
 * del mismo sonido es la manera de que el alumno apague la fanfarria y la fanfarria siga.
 *
 * `volumen` es el de la casa, no el del fichero: todos los ficheros salen normalizados al mismo
 * pico, así que lo que equilibra un golpe contra un clic se decide aquí y se oye aquí.
 *
 * Y `varia` DICE SI ESE SONIDO PUEDE SALIR DESAFINADO A PROPÓSITO. El banco mueve el tono de
 * cada disparo un ±4,5 % —**76 cents, tres cuartos de semitono**— para que un golpe repetido
 * cuarenta veces en un combate siga llegando: el oído tapa lo que da por sabido. Eso vale para
 * un impacto y es un error en todo lo demás, y el 17 de octubre de 2026 Gabri lo oyó en el
 * repaso: *«no es idéntico, la segunda vez suena más desafinado y más grave»*. Tenía razón, y
 * el apaño estaba escrito pero no conectado — la puerta existía y no la usaba nadie.
 *
 * **La decisión es de la muestra, no de quien la dispara**, y por eso vive aquí al lado del
 * canal y del volumen: quien pide un acierto no tiene por qué saber que existe una ruleta de
 * afinación. Por defecto NO varía. Varían los tres impactos del combate y nada más.
 */
export const MUESTRAS = {
  // Los del combate, que son los que lo pidieron.
  acierto: { fichero: "juego/acierto", canal: CANAL.JUEGO, volumen: 0.8 },
  fallo: { fichero: "juego/fallo", canal: CANAL.JUEGO, volumen: 0.85 },
  hit8bit: { fichero: "combate/golpe", canal: CANAL.JUEGO, volumen: 0.9, varia: true },
  // LOS TRES DE LA ESTRELLA, uno por habilidad, **y las claves son las de `lib/combate.mjs`**: la
  // carta, la forma que pinta `ataques3d.js` y el sonido se llaman igual porque son la misma cosa
  // dicha tres veces. Gabri, el 13 de octubre: *«los sonidos de la estrella deberían sonar más
  // intergalácticos o estelares: chispas, rayos y truenos como tal. No empujones o golpes»*. Y lo
  // que se mide le da la razón: `golpe` y `dano` tienen el 98% de su energía por debajo de 120 Hz
  // —son puñetazos— y estos tres no comparten banda ni entre ellos ni con el del fantasma, así que
  // se distinguen hasta sonando encima de la música.
  rayo: { fichero: "combate/rayo", canal: CANAL.JUEGO, volumen: 0.85 },
  chispitas: { fichero: "combate/chispitas", canal: CANAL.JUEGO, volumen: 0.6 },
  radiacion: { fichero: "combate/radiacion", canal: CANAL.JUEGO, volumen: 0.85 },
  hurt8bit: { fichero: "combate/dano", canal: CANAL.JUEGO, volumen: 0.9, varia: true },
  punch: { fichero: "combate/choque", canal: CANAL.JUEGO, volumen: 1, varia: true },
  victory8bit: { fichero: "juego/victoria", canal: CANAL.JUEGO, volumen: 0.85 },
  pierdes: { fichero: "juego/derrota", canal: CANAL.JUEGO, volumen: 0.8 },
  // EL DERRUMBE NO SUSTITUYE A `pierdes`, CONVIVE CON ÉL. `derrota` son cuatro notas bajando y
  // suena bien **sola**, que es como suena en los minijuegos. Encima de la música del cierre no:
  // dos melodías en tonalidades distintas se pelean siempre. Esto es ruido —una masa grave que se
  // desploma— y por eso cabe debajo de cualquier acorde. Lo pide la pantalla del final, y sólo ella.
  derrumbe: { fichero: "combate/derrumbe", canal: CANAL.JUEGO, volumen: 0.9 },
  // EL REPASO TIENE SU PROPIO ACIERTO, y se llamaba `palmas` hasta el 14 de octubre de 2026.
  // Gabri eligió para él una palmada de verdad —una sola, sin repetición— y al oírla dijo lo
  // que hacía falta oír: *«no es un sonido de palmas como tal, es otro sonido de acierto»*.
  // Tenía razón, así que la clave dice lo que es. **Repasar algo que fallaste hace tres días no
  // es acertar una pregunta nueva**, y por eso suena distinto que en la lección; y es UNO y no
  // dos, que es lo que de verdad sonaba mal: hasta hoy el repaso disparaba `acierto` y encima
  // las palmas, dos sonidos amontonados.
  repasada: { fichero: "juego/repasada", canal: CANAL.INTERFAZ, volumen: 0.85 },
  // EL JALEO VA DEBAJO DE LA VICTORIA, no en su lugar. Una sala animando sin música no es una
  // victoria, es ruido; la fanfarria sola no tiene a nadie delante. Son dos ficheros y los junta
  // la pantalla al dispararlos a la vez, que es lo que permite apagar uno sin perder el otro.
  jaleo: { fichero: "juego/jaleo", canal: CANAL.JUEGO, volumen: 0.45 },
  // LOS DOS QUE ESPERAN SU PANTALLA, y están aquí para que el portero los vigile mientras tanto.
  // `nivel` es la ceremonia de subir de nivel que Gabri pidió el 14 de octubre —*«debe aparecer
  // una pantalla con la estrella brillando, de 3 a 5 segundos»*— y `fogonazo` es el poder que da
  // Mnemo a las quince lecciones (M21). Ninguno suena todavía **porque su pantalla no existe**:
  // el día que exista, sólo hay que llamarlos.
  nivel: { fichero: "juego/nivel", canal: CANAL.JUEGO, volumen: 0.8 },
  fogonazo: { fichero: "mundo/fogonazo", canal: CANAL.JUEGO, volumen: 0.8 },
  // Y dos de la casa entera, que suenan en todas partes.
  whoosh: { fichero: "interfaz/whoosh", canal: CANAL.INTERFAZ, volumen: 0.7 },
  tick: { fichero: "interfaz/clic", canal: CANAL.INTERFAZ, volumen: 0.45 },
  // LOS DOS QUE NO EXISTÍAN: la voz de cada contendiente en el cartel de batalla. No están en
  // `sound.js` —no hay oscilador que haga un gruñido— y los pide el cartel directamente.
  rival: { fichero: "combate/rival", canal: CANAL.JUEGO, volumen: 0.9 },
  // LOS DE PIXABAY DEL 27 DE OCTUBRE (B3), que Gabri eligió oyendo la tabla: el rugido de Asterión
  // —*«no es un rugido»* decía del sintetizado—, el cofre y su redoble, el pájaro y la voz base de
  // los personajes, que se cambió por sus frases de verdad el 29 (abajo).
  rugido: { fichero: "mundo/rugido", canal: CANAL.JUEGO, volumen: 0.9 },
  cofreEmpieza: { fichero: "mundo/cofre", canal: CANAL.JUEGO, volumen: 0.7 },
  cofreRedoble: { fichero: "mundo/redoble", canal: CANAL.JUEGO, volumen: 0.75 },
  pajaro: { fichero: "mundo/pajaro", canal: CANAL.JUEGO, volumen: 0.75 },
  // LAS FRASES DE LOS PERSONAJES (29 de octubre de 2026), de los audios del capítulo 1 del lore,
  // elegidas por Gabri oyéndolas. Cuál toca cada vez lo dice `lib/vocesDeLosPersonajes.mjs`.
  fraseUlises1: { fichero: "voces/frase-ulises-1", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseUlises2: { fichero: "voces/frase-ulises-2", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseUlises3: { fichero: "voces/frase-ulises-3", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseUlises4: { fichero: "voces/frase-ulises-4", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseAriadna1: { fichero: "voces/frase-ariadna-1", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseAriadna2: { fichero: "voces/frase-ariadna-2", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseAriadna3: { fichero: "voces/frase-ariadna-3", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseAriadna4: { fichero: "voces/frase-ariadna-4", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates1: { fichero: "voces/frase-socrates-1", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates2: { fichero: "voces/frase-socrates-2", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates3: { fichero: "voces/frase-socrates-3", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates4: { fichero: "voces/frase-socrates-4", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates5: { fichero: "voces/frase-socrates-5", canal: CANAL.JUEGO, volumen: 0.9 },
  fraseSocrates6: { fichero: "voces/frase-socrates-6", canal: CANAL.JUEGO, volumen: 0.9 },
  // LOS BRILLOS DEL ÁGORA (28 de octubre de 2026): *«sonidos luminosos al pulsar sobre algún
  // lugar»*. Uno por cada luz: el rojo de las puertas, el oro de la Liga y el de tu Estrella.
  brillo: { fichero: "juego/brillo", canal: CANAL.JUEGO, volumen: 0.55 },
  brilloOro: { fichero: "juego/brillo-oro", canal: CANAL.JUEGO, volumen: 0.55 },
  brilloEstrella: { fichero: "juego/brillo-estrella", canal: CANAL.JUEGO, volumen: 0.55 },
  estrella: { fichero: "combate/estrella", canal: CANAL.JUEGO, volumen: 0.8 },
};

/** Si un sonido tiene fichero. Lo pregunta `sound.js` antes de sintetizar. */
export const hayMuestra = (nombre) => Object.prototype.hasOwnProperty.call(MUESTRAS, nombre);

/** El canal de una muestra; el de JUEGO si alguien pregunta por una que no existe. */
export const canalDeLaMuestra = (nombre) => (MUESTRAS[nombre] || {}).canal || CANAL.JUEGO;

/** Lo que hay que bajarse, con la extensión que el navegador haya dicho que entiende. */
export const rutaDe = (nombre, ext) => `${CARPETA}${MUESTRAS[nombre].fichero}.${ext}`;

/**
 * QUÉ FORMATO SE VA A USAR, preguntándoselo al navegador una sola vez. `puede` es su
 * `canPlayType`: devuelve `"probably"`, `"maybe"` o **la cadena vacía**, que es su manera de
 * decir que no. Si no puede con ninguno, `null`, y entonces no se pide nada: la casa sintetiza
 * como siempre y nadie se entera de que había ficheros.
 */
export function tipoQueSuena(puede) {
  for (const f of FORMATOS) {
    const r = puede(f.tipo);
    if (r === "probably" || r === "maybe") return f.ext;
  }
  return null;
}

/**
 * LAS PISTAS DE MUSICA, que son otra cosa y por eso van en su propia tabla: no se disparan, se
 * ponen; no se decodifican enteras en memoria —veinticinco segundos serían cinco megas—, se
 * reproducen desde el fichero; y una de ellas gira en bucle hasta que alguien la pare.
 *
 * Y VAN AL CANAL DEL JUEGO, que fue una corrección: el 13 de octubre escribí que la música debía
 * tener **su propio interruptor**, con el argumento de que quien quiere efectos sin música no
 * tiene por qué elegir. El argumento sigue siendo bueno y la decisión estaba tomada antes de que
 * yo llegara: la pantalla de Ajustes lleva meses ofreciendo tres valores y el de en medio se
 * llama, con todas sus letras, **«Sin música ni fanfarrias»**. Un cuarto canal habría dejado el
 * ajuste prometiendo una cosa y haciendo otra, que es exactamente la avería que `canalesDeSonido`
 * vino a arreglar. Lo que está escrito en la pantalla manda sobre lo que se me ocurra a mí.
 */
export const PISTAS = {
  opening: { fichero: "musica/opening", canal: CANAL.JUEGO, volumen: 0.85, bucle: false },
  // EL LOOP SUENA MÁS BAJO QUE EL OPENING, y no es lo mismo que haberlo mezclado peor: el opening
  // **es** la pantalla y éste va debajo de una pregunta que hay que leer. Gabri lo probó jugando:
  // *«me distrae de las preguntas»*. Lo primero fue quitarle los graves —el 72% de su energía
  // estaba entre 60 y 120 Hz, pisándose el bajo con el tambor— y lo segundo, bajarlo aquí.
  combate: { fichero: "musica/combate", canal: CANAL.JUEGO, volumen: 0.42, bucle: true },
  // LA DEL FINAL ES DE GABRI, tocada con su teclado el 13 de octubre. La grabó para el opening y
  // allí no encajaba —cuatro compases de la misma intensidad: es un loop, no un opening—, pero
  // tiene lo único que no tiene ninguna de las otras dos: **sensación de desenlace**. Once segundos
  // y medio, que es casi exactamente lo que se tarda en leer un resultado, y sin bucle: cuando
  // acaba, el silencio que queda es la entrada de la ceremonia.
  final: { fichero: "musica/final", canal: CANAL.JUEGO, volumen: 0.8, bucle: false },
  // LA CUENTA DEL DUELO DE CLASE (G20): veinte segundos justos, lo que dura una pregunta, escritos
  // contra el reloj en `tools/haz_musica.py`. Sin bucle: si la pregunta se cierra antes, se funde;
  // si llega a cero, la pista acaba ahí sola. Suena en la pizarra, nunca en los Chromebook.
  cuenta: { fichero: "musica/cuenta", canal: CANAL.JUEGO, volumen: 0.7, bucle: false },
};

/** Si existe esa pista. */
export const hayPista = (nombre) => Object.prototype.hasOwnProperty.call(PISTAS, nombre);

/** Lo que hay que bajarse para una pista, con la extensión que el navegador entienda. */
export const pistaDe = (nombre, ext) => `${CARPETA}${PISTAS[nombre].fichero}.${ext}`;

/** Todo lo que hay que precargar para que el combate no llegue tarde a su primer golpe. */
export const LAS_DEL_COMBATE = ["acierto", "fallo", "hit8bit", "hurt8bit", "punch", "rival",
  "estrella", "rayo", "chispitas", "radiacion"];

export default MUESTRAS;
